import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  Validators
} from '@angular/forms';
import { BankDataService } from '@core/services/http-services/bank-data.service';
import { BankAccount, setWhiteSpacesInIban } from '@models/bank-data';
import { ValidatorIBAN } from '@utils/custom-validators';
import { spinner } from '@utils/rxjs';
import { removeWhiteSpaces } from '@utils/utils';
import { of, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Component({
  selector: 'mapfre-iban-input',
  templateUrl: './mapfre-iban-input.component.html',
  styleUrls: ['./mapfre-iban-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MapfreIbanInputComponent),
      multi: true
    }
  ]
})
export class MapfreIbanInputComponent implements OnInit, ControlValueAccessor {
  @Output() onValidBankAccount = new EventEmitter<BankAccount>();
  @Output() onInvalidBankAccount = new EventEmitter();
  @Input() calculateBankName = true;
  @Input() ibanMaxLength = 24;
  @Input() prefix = 'ES';

  onChange: Function = (value: string) => {};
  onTouched: Function = () => {};
  inputMaxLength: number;
  bankEntitySuscription: Subscription;
  bankName: string;
  lastInput = '';
  maxLengthReached: number;
  lastIntroducedIban = '';

  ibanForm = new FormGroup({
    iban: new FormControl(this.prefix, [Validators.required, ValidatorIBAN])
  });

  constructor(private bankDataService: BankDataService) {}

  writeValue(value: BankAccount): void {
    if (value) {
      this.bankName = value.bank;
      this.ibanForm.patchValue({ iban: setWhiteSpacesInIban(value.iban) }, { emitEvent: false });
    }
  }

  registerOnChange(fn: Function): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.ibanForm.disable() : this.ibanForm.enable();
  }

  ngOnInit() {
    const numberOfWhiteSpacesInParsedIban = this.ibanMaxLength / 4 - 1;
    this.inputMaxLength = this.ibanMaxLength + numberOfWhiteSpacesInParsedIban;
    this.maxLengthReached = this.prefix.length;
    this.lastIntroducedIban = this.prefix;

    this.ibanForm.valueChanges.subscribe(({ iban }) => {
      const flatIban = removeWhiteSpaces(iban);
      if (iban.length <= this.prefix.length) {
        iban = this.prefix;
        this.maxLengthReached = this.prefix.length;
      } else if (!flatIban.startsWith(this.prefix)) {
        iban = this.lastIntroducedIban;
      } else if (
        flatIban.length >= this.maxLengthReached ||
        flatIban.length === this.ibanMaxLength
      ) {
        this.maxLengthReached = flatIban.length;
        iban = setWhiteSpacesInIban(flatIban);
      } else {
        return;
      }

      this.lastIntroducedIban = iban;
      this.ibanForm.patchValue({ iban }, { emitEvent: false });
      this.checkFormValidity();
      this.onChange(this.ibanForm.get('iban').value);
      this.onTouched();
    });
  }

  onPaste(event: ClipboardEvent) {
    let clipBoardText = event.clipboardData.getData('text');
    clipBoardText = removeWhiteSpaces(clipBoardText);
    let iban = /[a-zA-Z]/.test(clipBoardText.substr(0, this.prefix.length))
      ? this.prefix + clipBoardText.slice(this.prefix.length)
      : clipBoardText;
    this.maxLengthReached = iban.length;
    iban = setWhiteSpacesInIban(iban);
    this.lastIntroducedIban = iban;
    this.ibanForm.patchValue({ iban }, { emitEvent: false });
    event.preventDefault();
    this.checkFormValidity();
  }

  validateAndGetEntityName(iban: string) {
    iban = removeWhiteSpaces(iban).toUpperCase();
    this.bankName = '';
    this.bankEntitySuscription = this.bankDataService
      .validateIBAN(iban)
      .pipe(
        switchMap(({ id }) => {
          if (this.calculateBankName) {
            return this.bankDataService.getBankOfficeDirection({
              officeId: id.substr(4, 4),
              entityCode: id.substr(0, 4)
            });
          }
          return of(null);
        }),
        spinner('ibanValidation')
      )
      .subscribe(
        response => {
          this.bankName = response?.name;
          const accountToSend = {
            bank: this.bankName,
            iban
          };
          this.onValidBankAccount.emit(accountToSend);
        },
        () => {
          this.ibanForm.controls['iban'].setErrors({
            incorrect: true
          });
          this.onInvalidBankAccount.emit();
        }
      );
  }

  private checkFormValidity() {
    if (this.ibanForm.valid) {
      const currentIbanText = this.ibanForm.get('iban').value;
      this.validateAndGetEntityName(currentIbanText);
    } else {
      this.onInvalidBankAccount.emit();
      this.bankEntitySuscription?.unsubscribe();
    }
  }
}
